import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';

interface PasswordStrengthIndicatorProps {
  password: string;
}

interface PasswordCriteria {
  label: string;
  met: boolean;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ password }) => {
  // Calculate password criteria
  const criteria: PasswordCriteria[] = [
    {
      label: '8+',
      met: password.length >= 8
    },
    {
      label: 'A-Z',
      met: /[A-Z]/.test(password)
    },
    {
      label: 'a-z',
      met: /[a-z]/.test(password)
    },
    {
      label: '0-9',
      met: /[0-9]/.test(password)
    },
    {
      label: '!@#',
      met: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    }
  ];

  // Calculate strength score (0-5)
  const strengthScore = criteria.filter(c => c.met).length;
  
  // Calculate strength percentage
  const strengthPercentage = (strengthScore / 5) * 100;
  
  // Determine strength level and color
  const getStrengthLevel = () => {
    if (strengthScore === 0) return { level: '', color: '#E0E0E0' };
    if (strengthScore === 1) return { level: 'Weak', color: '#FF5722' };
    if (strengthScore === 2) return { level: 'Fair', color: '#FF9800' };
    if (strengthScore === 3) return { level: 'Good', color: '#FF9800' };
    if (strengthScore === 4) return { level: 'Strong', color: '#4CAF50' };
    if (strengthScore === 5) return { level: 'Very Strong', color: '#4CAF50' };
    return { level: '', color: '#E0E0E0' };
  };

  const { level, color } = getStrengthLevel();

  return (
    <Box
      sx={{
        backgroundColor: '#F5F5F5',
        borderRadius: '8px',
        padding: '12px 16px',
        marginTop: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        minHeight: '40px'
      }}
    >
      {/* Left side - Criteria */}
      <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
        {criteria.map((criterion, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Box
              sx={{
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                backgroundColor: criterion.met ? '#4CAF50' : '#FFFFFF',
                border: '2px solid',
                borderColor: criterion.met ? '#4CAF50' : '#E0E0E0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '10px',
                color: '#FFFFFF',
                fontWeight: 'bold'
              }}
            >
              {criterion.met && '✓'}
            </Box>
            <Typography
              variant="body2"
              sx={{
                fontSize: '12px',
                fontWeight: '500',
                color: '#333333'
              }}
            >
              {criterion.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Right side - Progress bar and strength level */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '120px' }}>
        <Box sx={{ flex: 1, minWidth: '60px' }}>
          <LinearProgress
            variant="determinate"
            value={strengthPercentage}
            sx={{
              height: '6px',
              borderRadius: '3px',
              backgroundColor: '#E0E0E0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: color,
                borderRadius: '3px'
              }
            }}
          />
        </Box>
        {level && (
          <Typography
            variant="body2"
            sx={{
              fontSize: '12px',
              fontWeight: '600',
              color: color,
              minWidth: '40px'
            }}
          >
            {level}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PasswordStrengthIndicator;
